import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '../../hooks/useQuery';
import { api, type LineageSource, type LineageTab } from '../../lib/api';
import { Spinner, Modal, ActionButton } from '../ui';
import { useDirtyGuard } from '../../hooks/useDirtyGuard';
import { SaveStatus, useSaveState } from '../SaveStatus';

/**
 * "Where did this number come from, where do I go to change it, and what if I
 * can't change the source at all?"
 *
 * A calculated cell is only useful if you can act on it. This shows the formula
 * the recompute actually used, the inputs it used them with, the lines
 * immediately below it, and — the part that matters — the real records behind
 * it, each with a button to the surface that owns it.
 *
 * It is also where a line override is set. Overriding is the escape hatch for
 * when the source genuinely cannot be edited; it is deliberately not frictionless
 * (a reason is required) and it always shows the computed figure beside the
 * override, so nobody loses sight of what the model actually says.
 *
 * Everything displayed is read from what the last recompute WROTE, never
 * re-derived here: if the stored lineage disagrees with the current inputs, that
 * is a stale recompute, and recomputing in this panel would hide it.
 */

const money = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString(undefined, { maximumFractionDigits: 0 });

const TAB_LABEL: Record<LineageTab, string> = {
  grid: 'Budget Grid', drivers: 'Drivers', revenue: 'Revenue', people: 'People', formulas: 'Formulas',
};
const KIND_LABEL: Record<LineageSource['kind'], string> = {
  driver: 'Driver', item: 'Item', contract: 'Contract', headcount: 'Headcount', formula: 'Formula',
};

const row: React.CSSProperties = { display: 'flex', gap: 8, alignItems: 'baseline', padding: '3px 0', fontSize: 12 };
const num: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', marginLeft: 'auto', fontWeight: 500 };
const sub: React.CSSProperties = { fontSize: 11, color: 'var(--color-text-muted)' };
const mono: React.CSSProperties = { fontFamily: 'var(--font-mono, monospace)', fontSize: 11 };
const box: React.CSSProperties = {
  fontSize: 12, padding: '4px 7px', border: '0.5px solid var(--color-border-strong)',
  borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)', color: 'var(--color-text)',
};
const sectionHead: React.CSSProperties = {
  fontSize: 10, textTransform: 'uppercase', letterSpacing: '.05em',
  color: 'var(--color-text-muted)', marginTop: 14, marginBottom: 4,
};

export default function CellLineage({ planId, line, dept, month, canOverride, onClose, onOpenItem, onChanged }: {
  planId: string;
  line: string;
  dept: string | null;
  month: string;
  /** Draft plan + write access. Overriding is an edit like any other. */
  canOverride: boolean;
  onClose: () => void;
  /** Opens the item editor in place rather than navigating away. */
  onOpenItem?: (itemId: string) => void;
  /** The override changed the plan's lines; the grid needs to refetch. */
  onChanged?: () => void;
}) {
  const q = useQuery(() => api.fpa.plans.lineage(planId, line, month, dept), [planId, line, month, dept]);
  const navigate = useNavigate();
  const guard = useDirtyGuard();
  const save = useSaveState();
  const [editing, setEditing] = useState(false);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');

  const explain = q.data?.explain ?? null;
  const override = explain?.override ?? null;

  // Navigating away unmounts the grid, so buffered cells would be lost.
  function goTo(tab: LineageTab) {
    if (!guard.confirmLeave()) return;
    onClose();
    navigate(`/admin/fpa/plans/${planId}/w/${tab}`);
  }

  function startEditing() {
    setAmount(String(override?.amount ?? explain?.amount ?? ''));
    setReason(override?.reason ?? '');
    setEditing(true);
  }

  async function applyOverride() {
    const n = Number(amount);
    if (!Number.isFinite(n)) { save.fail('Enter a number to override with.'); return; }
    if (!reason.trim()) { save.fail('A reason is required — record why this is not the computed number.'); return; }
    const ok = await save.run(
      () => api.fpa.plans.setLineOverride(planId, {
        line_code: line, department_id: dept, period_month: month, amount: n, reason: reason.trim(),
      }),
      'override set',
    );
    if (!ok) return;
    setEditing(false);
    q.refetch();
    onChanged?.();
  }

  async function clearOverride() {
    const ok = await save.run(() => api.fpa.plans.clearLineOverride(planId, line, month, dept), 'override cleared');
    if (!ok) return;
    setEditing(false);
    q.refetch();
    onChanged?.();
  }

  return (
    <Modal
      title={explain ? explain.label : line}
      subtitle={`${month}${dept ? '' : ' · consolidated'}${explain?.is_rollup ? ' · rolled up from departments' : ''}`}
      onClose={onClose}
      width={560}
    >
      <div style={{ padding: 20 }}>
        {q.isLoading && <Spinner />}
        {!q.isLoading && !explain && (
          <p style={sub}>No computed value stored for this cell. Recompute the plan from the Review tab if you expect one.</p>
        )}

        {explain && (
          <>
            <div style={{ ...row, borderBottom: '0.5px solid var(--color-border)', paddingBottom: 8 }}>
              <span style={{ fontWeight: 600 }}>Value</span>
              <span style={{ ...num, fontSize: 14 }}>{money(explain.amount)}</span>
            </div>

            {override && (
              <div style={{ marginTop: 10, padding: '8px 10px', background: 'rgba(240,180,0,0.14)', borderRadius: 'var(--radius-sm)' }}>
                <div style={row}><span style={{ fontWeight: 600 }}>Overridden</span></div>
                <div style={row}><span style={sub}>Computed</span><span style={num}>{money(override.computed)}</span></div>
                <div style={row}><span style={sub}>Override</span><span style={num}>{money(override.amount)}</span></div>
                {override.computed != null && (
                  <div style={row}>
                    <span style={sub}>Difference</span>
                    <span style={num}>{override.amount - override.computed > 0 ? '+' : ''}{money(override.amount - override.computed)}</span>
                  </div>
                )}
                <div style={{ ...row, display: 'block' }}>
                  <div style={sub}>Reason</div>
                  <div style={{ fontSize: 12 }}>{override.reason ?? '—'}</div>
                </div>
                <div style={row}><span style={sub}>Set by {override.set_by ?? 'unknown'} · {override.set_at.slice(0, 10)}</span></div>
              </div>
            )}

            {explain.rederived_after_override && !override && (
              <p style={{ ...sub, marginTop: 8 }}>
                This subtotal was re-derived because a line above it is overridden, so the statement still foots.
              </p>
            )}

            {explain.formula && (
              <>
                <div style={sectionHead}>How it is calculated</div>
                <div style={{ ...mono, padding: '4px 8px', background: 'var(--color-surface-alt, rgba(120,120,120,0.06))', borderRadius: 'var(--radius-sm)' }}>
                  {explain.formula}
                </div>
              </>
            )}

            {explain.inputs.length > 0 && (
              <>
                <div style={sectionHead}>With these inputs</div>
                {explain.inputs.map((i) => (
                  <div key={i.key} style={row}>
                    <span style={mono}>{i.key}</span>
                    <span style={sub}>{i.label}</span>
                    <span style={num}>{money(i.value)}</span>
                  </div>
                ))}
              </>
            )}

            {explain.contributing_lines.length > 0 && (
              <>
                <div style={sectionHead}>Made up of</div>
                {explain.contributing_lines.map((c) => (
                  <div key={c.line_code} style={row}>
                    <span>{c.label}</span>
                    <span style={{ ...sub, ...mono }}>{c.line_code}</span>
                    <span style={num}>{money(c.amount)}</span>
                  </div>
                ))}
              </>
            )}

            <div style={sectionHead}>To change it, go to</div>
            {explain.sources.length === 0 && (
              <p style={sub}>
                Nothing editable feeds this cell directly — it is a subtotal. Follow one of the lines above to its own inputs.
              </p>
            )}
            {explain.sources.map((s, idx) => (
              <div key={`${s.kind}-${s.id ?? idx}`} style={{ ...row, alignItems: 'center' }}>
                <span style={{ ...sub, width: 74, flexShrink: 0 }}>{KIND_LABEL[s.kind]}</span>
                <span>{s.label}</span>
                {s.detail && <span style={{ ...sub, ...mono }}>{s.detail}</span>}
                <span style={{ marginLeft: 'auto' }}>
                  {s.kind === 'item' && s.id && onOpenItem
                    ? <ActionButton onClick={() => { onClose(); onOpenItem(s.id!); }}>Edit item</ActionButton>
                    : <ActionButton onClick={() => goTo(s.tab)}>{TAB_LABEL[s.tab]}</ActionButton>}
                </span>
              </div>
            ))}

            {canOverride && (
              <>
                <div style={sectionHead}>Or override it</div>
                {!editing && (
                  <div style={{ ...row, alignItems: 'center' }}>
                    <span style={sub}>
                      Use this when the source cannot practically be edited. Subtotals below will follow.
                    </span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                      <ActionButton onClick={startEditing}>{override ? 'Change override' : 'Override'}</ActionButton>
                      {override && <ActionButton variant="danger" onClick={clearOverride} disabled={save.busy}>Clear</ActionButton>}
                    </span>
                  </div>
                )}
                {editing && (
                  <>
                    <div style={row}>
                      <span style={{ ...sub, width: 74 }}>Amount</span>
                      <input autoFocus value={amount} onChange={(e) => setAmount(e.target.value)} style={{ ...box, width: 140 }} />
                      <span style={sub}>computed {money(override?.computed ?? explain.amount)}</span>
                    </div>
                    <div style={row}>
                      <span style={{ ...sub, width: 74 }}>Reason</span>
                      <input value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="why this is not the computed number" style={{ ...box, flex: 1 }} />
                    </div>
                    <div style={{ ...row, alignItems: 'center' }}>
                      <ActionButton variant="primary" onClick={applyOverride} disabled={save.busy}>
                        {save.busy ? 'Saving…' : 'Save override'}
                      </ActionButton>
                      <ActionButton onClick={() => setEditing(false)}>Cancel</ActionButton>
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}

        <div style={{ display: 'flex', marginTop: 18, alignItems: 'center', gap: 8 }}>
          <SaveStatus state={save} />
          <span style={{ marginLeft: 'auto' }}><ActionButton onClick={onClose}>Close</ActionButton></span>
        </div>
      </div>
    </Modal>
  );
}
